import imageCompression from 'browser-image-compression';

export function isHeic(file: File): boolean {
  return /heic|heif/i.test(file.type) || /\.(heic|heif)$/i.test(file.name);
}

async function heicToJpeg(file: File): Promise<File> {
  const { default: heic2any } = await import('heic2any');
  const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
  const single = Array.isArray(blob) ? blob[0] : blob;
  if (!single) throw new Error('HEIC conversion produced no output');
  return new File([single], file.name.replace(/\.(heic|heif)$/i, '.jpg'), {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}

export async function compressForUpload(file: File): Promise<File> {
  let working = file;

  if (isHeic(working)) {
    working = await heicToJpeg(working);
  }

  if (working.size < 700 * 1024 && working.type === 'image/jpeg') {
    return working;
  }

  const compressed = await imageCompression(working, {
    maxSizeMB: 1.2,
    maxWidthOrHeight: 2048,
    useWebWorker: true,
    fileType: 'image/jpeg',
    initialQuality: 0.85,
    alwaysKeepResolution: false,
  });

  return new File([compressed], working.name.replace(/\.[^.]+$/, '.jpg'), {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}

/**
 * Aggressive second-pass compression used when the server returns
 * IMAGE_TOO_LARGE on a file that was already through compressForUpload.
 * Drops to 1200px max and quality 0.7 — legible menu text survives both.
 */
export async function extraCompressForUpload(file: File): Promise<File> {
  let working = file;
  if (isHeic(working)) {
    working = await heicToJpeg(working);
  }
  const compressed = await imageCompression(working, {
    maxSizeMB: 0.6,
    maxWidthOrHeight: 1200,
    useWebWorker: true,
    fileType: 'image/jpeg',
    initialQuality: 0.7,
    alwaysKeepResolution: false,
  });
  return new File([compressed], working.name.replace(/\.[^.]+$/, '.jpg'), {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}
